<template>
  <b-card v-loading="loading">
    <h4>
      Images <small>(up to {{maxImages}})</small>
      <div class="pull-right">
        <b-link v-if="imageShuffle" class="text-primary toggle-shuffle" @click="toggleShuffle()" v-b-tooltip.hover title="Images will be shuffled randomly before posting. Click to disable shuffle."><i class="icon-shuffle"></i></b-link>
        <b-link v-else class="text-secondary toggle-shuffle" @click="toggleShuffle()" v-b-tooltip.hover title="Images will be posted in the order presented here. Click to enable shuffle."><i class="icon-shuffle"></i></b-link>
      </div>
    </h4>
    <b-row class="mt-3">
      <b-col v-for="(image, key) in images" :key="key" :sm="3" class="d-flex mb-3">
        <b-card class="image-card" body-class="d-flex flex-grow-1 flex-column justify-content-center align-items-center clickable" v-loading="image.loading" @click="deleteImage(image)">
          <img :src="image.url" />
          <div class="delete text-danger"><i class="cui-trash"></i></div>
        </b-card>
      </b-col>
      <b-col v-if="images.length < maxImages" :sm="3" class="d-flex mb-3">
        <b-card class="image-card clickable" body-class="d-flex flex-grow-1 flex-column justify-content-center align-items-center">
          <b-form-file v-model="newImage" ref="newImageInput" class="image-upload" />
          <i class="icon-plus font-3xl text-light-grey d-block"></i>
        </b-card>
      </b-col>
    </b-row>
    <b-alert :show="!!error" variant="danger" class="mt-2 mb-0 py-2 px-3 d-flex align-items-center">
      <i class="icon-info mr-2"></i> {{error}}
    </b-alert>
  </b-card>
</template>

<script>
const MAX_IMAGE_SIZE = 1024 * 1024 * 5 // 5MB

export default {
  name: 'Details',

  props: {
    listingId: {
      type     : Number,
      required : true,
    },
    images: {
      type     : Array,
      required : true,
    },
    imageShuffle: {
      type     : Boolean,
      required : true,
    },
    loading: {
      type    : Boolean,
      default : false,
    }
  },

  data(){
    return {
      maxImages: this.$session.getLimit('imagesPerPost'),

      // State
      error    : '',
      newImage : null,
    }
  },

  watch: {
    newImage(){
      if( this.newImage !== null ){
        this.addNewImage()
      }
    }
  },

  methods: {
    addNewImage(){
      // Validation
      if(!/^image\/(png|gif|jpeg)$/i.test(this.newImage.type)){
        this.imageError = 'Please upload only images. We accept: jpg, png, and gif.'
        return
      }

      // Max size
      if( this.newImage.size > MAX_IMAGE_SIZE ){
        this.imageError = 'This image is too large. Maximum file size is 5MB.'
        return
      }

      let reader = new FileReader();
      let image = {
        loading: true,
      }
      let images = this.images.concat([image])
      this.$emit('update:images', images)

      reader.addEventListener("load", function(){
        // This doesn't automatically re-render, render is triggered by the reset
        image.url = reader.result
        this.$refs.newImageInput.reset()
      }.bind(this), false);

      this.uploadImage(this.newImage, image)
      reader.readAsDataURL(this.newImage);
    },

    uploadImage(file, image){
      image.loading = true
      this.$API.v2.Listings.UploadImage(file, this.listingId)
      .then((img) => {
        image.loading = false
        image.url = img.url
        image.temporary = img.temporary
      })
      .catch((err) => {
        this.imageError = err
      })
    },

    deleteImage(image){
      // Delete locally if we're new (the bucket will auto delete after a day)
      if( !this.listingId ){
        let images = this.images.filter((img) => image !== img)
        this.$emit('update:images', images)
        return
      }

      // Delete request
      image.loading = true
      this.imageError = ''
      this.$API.v2.Listings.DeleteImage(image.url, this.listingId)
      .then(() => {
        let images = this.images.filter((img) => image !== img)
        this.$emit('update:images', images)
      })
      .catch((err) => {
        this.imageError = err
      })
      .then(() => {
        image.loading = false
      })
    },

    toggleShuffle(){
      this.$emit('update:imageShuffle', !this.imageShuffle)
    }
  }
};
</script>

<style lang="scss" scoped>
.image-card {
  width: 100%;
  height: 100px;
  margin: 0;

  .card-body {
    padding: 0;
    overflow: hidden;
    position: relative;

    > img {
      max-height: 100%;
      max-width: 100%;
      transition: all 250ms ease-in-out;
    }

    .delete {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      font-size: 32px;
      display: flex;
      justify-content: center;
      align-items: center;
      background: rgba(255, 255, 255, 0.9);
      opacity: 0;
      transition: all 250ms ease-in-out;

      > i {
        transition: all 250ms ease-in-out;
        transform: scale(0, 0);
        transform-origin: center;
      }
    }

    &:hover {
      .delete {
        opacity: 1;

        > i {
          transform: scale(1, 1);
        }
      }
    }
  }
}

.toggle-shuffle {
  text-decoration: none;
}
</style>
